import * as Sentry from '@sentry/vue'
import { getOrGenerateAssignmentValue, LOCAL_AB_TESTS } from '@/utils/abtest.utils'
import mixpanelUtils from '@/utils/mixpanel.utils'

const GET_CURRENT_USER_ACTION = 'getCurrentUserAction'

/**
 * Validate and parse backend assignment value
 * @param {*} backendValue - Value from backend
 * @returns {number|null} Parsed value or null if invalid
 */
const parseBackendValue = (backendValue) => {
  if (backendValue === null || backendValue === undefined) {
    return null
  }

  const parsed = parseFloat(backendValue)
  const isValid = !Number.isNaN(parsed) && parsed >= -1 && parsed <= 100

  return isValid ? parsed : null
}

/**
 * Sync backend assignment value to localStorage
 * @param {object} config - Test config
 * @param {*} backendValue - Backend value
 * @returns {boolean} True if synced from backend
 */
const syncFromBackend = (config, backendValue) => {
  const parsed = parseBackendValue(backendValue)

  if (parsed !== null) {
    localStorage.setItem(config.storageKey, parsed.toString())
    return true
  }

  return false
}

/**
 * Sync localStorage assignment value to backend updates object
 * @param {string} testKey - Test key
 * @param {object} config - Test config
 * @param {object} updates - Updates object to populate
 * @returns {boolean} True if needs backend update
 */
const syncToBackend = (testKey, config, updates) => {
  const localValue = getOrGenerateAssignmentValue(testKey)

  if (localValue !== null && localValue !== undefined) {
    updates[config.backendField] = localValue
    return true
  }

  return false
}

/**
 * Sync single A/B test between localStorage and backend
 * @param {string} testKey - Test key
 * @param {object} currentUser - Current user object
 * @param {object} updates - Updates object to populate
 * @returns {object} Sync result { hasBackendSync, needsUpdate }
 */
const syncABTest = (testKey, currentUser, updates) => {
  const config = LOCAL_AB_TESTS[testKey]

  if (!config?.backendField) {
    return { hasBackendSync: false, needsUpdate: false }
  }

  const backendValue = currentUser.personalInformation?.[config.backendField]

  // Priority 1: Backend is source of truth (for cross-device consistency)
  const hasBackendSync = syncFromBackend(config, backendValue)
  if (hasBackendSync) {
    return { hasBackendSync: true, needsUpdate: false }
  }

  // Priority 2: Sync localStorage to backend
  const needsUpdate = syncToBackend(testKey, config, updates)

  return { hasBackendSync: false, needsUpdate }
}

/**
 * Vuex plugin to sync A/B test assignment values between localStorage and backend
 *
 * Hybrid Approach:
 * - Backend stores assignmentValue (0-100 number) as source of truth
 * - Variant is computed dynamically from assignmentValue + rolloutWeight
 * - Supports cross-device consistency AND dial-up/dial-down
 */
const localStorageABTestPlugin = (store) => {
  store.subscribeAction({
    after: async (action) => {
      if (action.type !== GET_CURRENT_USER_ACTION) {
        return
      }

      const { currentUser } = store.state

      if (!currentUser?.id) {
        return
      }

      try {
        const updates = {}
        let hasBackendSync = false
        let needsUpdate = false

        // Sync all A/B tests
        Object.keys(LOCAL_AB_TESTS).forEach((testKey) => {
          const result = syncABTest(testKey, currentUser, updates)
          hasBackendSync = hasBackendSync || result.hasBackendSync
          needsUpdate = needsUpdate || result.needsUpdate
        })

        // Batch update backend if needed
        if (needsUpdate) {
          await store.dispatch('patchCurrentUserAction', {
            userId: currentUser.id,
            personalInformation: updates,
          })
        }

        // Update Mixpanel super property if we synced from backend
        if (hasBackendSync) {
          mixpanelUtils.setAuthUIABTestSuperProps()
          mixpanelUtils.setOnboardingABTestSUperProps()
          mixpanelUtils.setMyProfileABTestSuperProps()
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: 'localStorageABTestPlugin',
            userId: currentUser?.id,
          },
        })
      }
    },
  })
}

export default localStorageABTestPlugin
